// Daily Briefing Bar (Aug 2026 Executive Command Center).
//
// Renders at the very top of the admin Dashboard so an authorized
// user can answer, within the FIRST viewport:
//   - What money is coming in?
//   - What needs my attention?
//   - What is happening today?
//   - What should I handle first?
//
// Composed entirely from data the dashboard already fetches — the
// existing DashboardFinance + DashboardOperations DTOs — so this
// component adds ZERO new API calls (RTK Query dedupes when
// FinanceOverview / OperationsCommandCenter call the same endpoints
// with the same args). One optional Parent Requests count uses the
// existing useListParentRequestsQuery; a 403 (no ClassSchedule_Edit
// permission) is handled silently by hiding the tile.
//
// Rules:
//   - Everything is location-aware via the shared `locationId` prop.
//   - Finance metrics are role-gated: users without Invoice_View
//     receive `null` from the finance endpoint, so those tiles
//     silently hide.
//   - Positive states shipped for every empty-good scenario per spec §32.
//   - No new backend endpoints. No sparkline / yesterday comparison
//     this pass (deferred per spec §6 / §7 — requires historical data).

import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, LinearProgress, IconButton, Tooltip, alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  useGetDashboardFinanceSummaryQuery,
  useGetDashboardOperationsQuery,
  useListParentRequestsQuery,
} from "../../Api/attoDeskApi";
import { dashTokens, toneMap, Tone } from "./dashboardTheme";
import { formatCurrency, formatCurrencyCompact } from "../../utils/currency";

const greetingForHour = (h: number = new Date().getHours()) =>
  h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";

const briefTitleForHour = (h: number = new Date().getHours()) =>
  h < 12 ? "Your Morning Brief" : h < 18 ? "Today at a Glance" : "Today's Wrap-Up";

const briefSubtitleForHour = (h: number = new Date().getHours()) =>
  h < 12 ? "Here's what needs your attention before the day gets busy."
  : h < 18 ? "Here's how today is progressing."
  : "Here's how today is wrapping up.";

interface Props {
  locationId: number | "all";
  firstName?: string;
}

const DailyBriefingBar: React.FC<Props> = ({ locationId, firstName }) => {
  const navigate = useNavigate();
  const nowHour = new Date().getHours();

  // Shared queries — RTK Query dedupes with FinanceOverview /
  // OperationsCommandCenter using the SAME args, so this component
  // adds no additional network round-trips.
  const {
    data: finData, isFetching: finFetching, refetch: refetchFin,
  } = useGetDashboardFinanceSummaryQuery(
    { location_id: locationId === "all" ? null : locationId },
    { pollingInterval: 120_000 },
  );
  const {
    data: opsData, isFetching: opsFetching, refetch: refetchOps,
  } = useGetDashboardOperationsQuery(
    { location_id: locationId === "all" ? null : locationId },
    { pollingInterval: 60_000 },
  );

  // Local "last updated" tracker — the installed RTK Query version
  // doesn't expose dataUpdatedAt on the hook return, so we stamp it
  // ourselves whenever new data arrives.
  const [lastUpdated, setLastUpdated] = useState<number>(0);
  useEffect(() => { if (opsData || finData) setLastUpdated(Date.now()); }, [opsData, finData]);
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);
  // Parent requests count — gated by ClassSchedule_Edit on the
  // backend; if the user lacks permission the query errors 403 and
  // we simply hide the tile.
  const { data: prData, error: prError } = useListParentRequestsQuery(
    { page: 1, per_page: 1, status: "needs_action" },
  );

  const finance: any = finData?.data || null;
  const ops: any = opsData?.data || null;
  const hasFinancePerm = !!finance;
  const isAdmin = !!ops?.scope?.is_admin;

  const summary = finance?.summary || {};
  const kpis = ops?.kpis || {};
  const alerts: any[] = ops?.alerts || [];

  // Parent Requests count (safe — silent hide on 403).
  const parentRequestsData: any = (prData as any)?.data || null;
  const parentRequestsCounts: any = (prData as any)?.counts || null;
  const pendingParentRequests = parentRequestsData
    ? (parentRequestsCounts?.needs_action ?? parentRequestsData.length ?? 0)
    : 0;
  const parentRequestsAvailable = !prError && !!prData;

  const refresh = () => { refetchFin(); refetchOps(); };
  const updatedLabel = useMemo(() => {
    if (!lastUpdated) return "";
    const secs = Math.max(0, Math.floor((Date.now() - lastUpdated) / 1000));
    if (secs < 45) return "Updated just now";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `Updated ${mins} min ago`;
    return `Updated ${dayjs(lastUpdated).format("h:mm A")}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdated, nowTick]);
  const isRefreshing = opsFetching || finFetching;

  // ── Top Priorities ranking (frontend display layer) ────────────────
  // Order: critical operational → parent requests → overdue finance →
  // starting-soon → attendance pending. Only include items whose
  // count/value is meaningful (spec §3, §4).
  type PriorityRow = {
    key: string; tone: Tone; icon: React.ReactNode;
    title: string; hint: string; onClick: () => void;
  };
  const priorities: PriorityRow[] = useMemo(() => {
    const out: PriorityRow[] = [];
    if ((kpis.needs_attention ?? 0) > 0) {
      out.push({
        key: "late", tone: "red", icon: <WarningAmberIcon />,
        title: `${kpis.needs_attention} class${kpis.needs_attention === 1 ? "" : "es"} not started`,
        hint: `${ops?.grace_minutes ?? 5}-min late threshold · review now`,
        onClick: () => document.getElementById("needs-attention-anchor")?.scrollIntoView({ behavior: "smooth" }),
      });
    }
    if (parentRequestsAvailable && pendingParentRequests > 0) {
      out.push({
        key: "parents", tone: "purple", icon: <ChatBubbleOutlineIcon />,
        title: `${pendingParentRequests} parent request${pendingParentRequests === 1 ? "" : "s"} need review`,
        hint: "Absence + makeup queue",
        onClick: () => navigate("/class-schedule/parent-requests"),
      });
    }
    if (hasFinancePerm && (summary.overdue_count ?? 0) > 0) {
      out.push({
        key: "overdue", tone: "amber", icon: <AccountBalanceWalletIcon />,
        title: `${summary.overdue_count} invoice${summary.overdue_count === 1 ? "" : "s"} overdue`,
        hint: `${formatCurrency(summary.outstanding)} outstanding total`,
        onClick: () => navigate("/invoice/list"),
      });
    }
    if ((ops?.upcoming_classes?.length ?? 0) > 0) {
      out.push({
        key: "soon", tone: "blue", icon: <AccessTimeIcon />,
        title: `${ops.upcoming_classes.length} class${ops.upcoming_classes.length === 1 ? "" : "es"} starting soon`,
        hint: "Next hour",
        onClick: () => document.getElementById("starting-soon-anchor")?.scrollIntoView({ behavior: "smooth" }),
      });
    }
    return out.slice(0, 4);
  }, [kpis.needs_attention, ops?.grace_minutes, ops?.upcoming_classes, parentRequestsAvailable, pendingParentRequests, hasFinancePerm, summary.overdue_count, summary.outstanding, navigate]);

  // ── Today's Progress ratios (spec §5) ──────────────────────────────
  const classesTotal = kpis.classes_today ?? 0;
  const classesDone = kpis.completed ?? 0;
  const classesPct = classesTotal > 0 ? Math.round((classesDone / classesTotal) * 100) : null;
  // Attendance submitted / expected — approximated as attendance
  // recorded rows vs enrolled seats. If the DTO can't tell us
  // reliably, we show `—` rather than fabricate.
  const attendanceExpected = kpis.student_seats ?? 0;
  const attendanceSubmitted = null;  // Not currently exposed by DTO — see report
  const attendancePct: number | null = null;
  const billedToday = summary.billed_today ?? 0;
  const paidToday = summary.paid_today ?? 0;
  const collectionsPct = billedToday > 0 ? Math.round((paidToday / billedToday) * 100) : null;

  // Compact metric pill used inside Morning Brief.
  const briefPill = (icon: React.ReactNode, value: React.ReactNode, label: string, tone: Tone) => {
    const t = toneMap[tone];
    return (
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        px: 1.5, py: 1, borderRadius: "12px",
        // Full solid tone fill (was near-white #FFFFFFCC) — spec v2
        // requires cards to be clearly the colour they represent, not
        // white with a tinted border.
        bgcolor: t.grad, border: `1px solid ${t.border}`,
        minWidth: 0, flex: "1 1 160px",
      }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: "10px",
          bgcolor: t.iconBg, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { fontSize: 16 }, flexShrink: 0,
          boxShadow: `0 4px 10px ${t.iconBg}35`,
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: t.accent, lineHeight: 1.1, letterSpacing: "-0.01em" }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: dashTokens.color.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mt: 0.25 }}>
            {label}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 1fr" },
      gap: 2, mb: 3,
    }}>
      {/* ══════════ Morning Brief ══════════ */}
      <Box sx={{
        position: "relative", overflow: "hidden",
        borderRadius: `${dashTokens.radius.lg}px`,
        p: { xs: 2.25, md: 2.75 },
        color: "#fff",
        // Solid navy — was a 135° linear-gradient; per 2026-09
        // Rainbow Colour Theory pass, every dashboard surface must be
        // a single solid colour. Navy remains the strongest anchor.
        bgcolor: dashTokens.color.navy,
        boxShadow: dashTokens.shadow.hero,
      }}>
        {/* Decorative blooms removed — the 2026-09 Full-Fill Rainbow
            v2 pass forbids floating decorative shapes / translucent
            corner circles on every dashboard surface, hero included. */}
        <Box sx={{ position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, gap: 1, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.14em" }}>
                {briefTitleForHour(nowHour)} · {dayjs().format("dddd, MMM D")}
              </Typography>
              <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, mt: 0.25, letterSpacing: "-0.02em" }}>
                {greetingForHour(nowHour)}{firstName ? `, ${firstName}` : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.85 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, mr: 0.5 }}>
                {isRefreshing ? "Refreshing…" : updatedLabel}
              </Typography>
              <Tooltip title="Refresh dashboard data" arrow>
                <IconButton size="small" onClick={refresh} sx={{ color: "#fff", "&:hover": { bgcolor: alpha("#fff", 0.14) } }}>
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 12.5, opacity: 0.8, mb: 2, maxWidth: 520 }}>
            {briefSubtitleForHour(nowHour)}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {/* Rainbow Colour Theory pass — each pill carries its own
                semantic tone so the first row of the dashboard reads
                as an intentional rainbow (spec §"TOP TODAY'S WRAP-UP"). */}
            {hasFinancePerm && briefPill(<PaymentsIcon />, formatCurrency(summary.paid_today), "Collected today", "emerald")}
            {briefPill(<CalendarTodayIcon />, classesTotal, "Classes today", "blue")}
            {briefPill(<GroupsOutlinedIcon />, kpis.students_expected ?? 0, "Students expected", "purple")}
            {(kpis.needs_attention ?? 0) > 0
              ? briefPill(<WarningAmberIcon />, kpis.needs_attention, "Need attention", "rose")
              : briefPill(<CheckCircleOutlineIcon />, "0", "Need attention", "mint")}
            {parentRequestsAvailable && pendingParentRequests > 0 &&
              briefPill(<ChatBubbleOutlineIcon />, pendingParentRequests, "Requests waiting", "purple")}
          </Box>
        </Box>
      </Box>

      {/* ══════════ Top Priorities — solid rose/coral fill (spec §2) */}
      <Box sx={{
        bgcolor: toneMap.rose.grad,
        border: `1px solid ${toneMap.rose.border}`,
        borderRadius: `${dashTokens.radius.lg}px`,
        boxShadow: dashTokens.shadow.card,
        p: 2, display: "flex", flexDirection: "column",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: "10px",
            bgcolor: toneMap.red.iconBg, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 12px ${toneMap.red.iconBg}40`,
            "& svg": { fontSize: 18 },
          }}>
            <BoltIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: dashTokens.color.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Top Priorities
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: dashTokens.color.text }}>
              {priorities.length > 0 ? `${priorities.length} to handle first` : "All clear"}
            </Typography>
          </Box>
        </Box>
        {priorities.length === 0 ? (
          <Box sx={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            textAlign: "center", py: 2.5,
            borderRadius: "12px",
            background: toneMap.green.grad,
            border: `1px solid ${toneMap.green.iconBg}30`,
          }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: toneMap.green.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
              <CheckCircleOutlineIcon />
            </Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: toneMap.green.accent }}>
              Everything looks good this morning.
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: dashTokens.color.muted, mt: 0.5 }}>
              No urgent action is required.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {priorities.map((p, i) => {
              const t = toneMap[p.tone];
              return (
                <Box key={p.key} onClick={p.onClick}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.25,
                    p: 1.25, borderRadius: "10px", cursor: "pointer",
                    bgcolor: alpha(t.iconBg, 0.06),
                    border: `1px solid ${alpha(t.iconBg, 0.20)}`,
                    transition: dashTokens.transition,
                    "&:hover": { transform: "translateX(2px)", bgcolor: alpha(t.iconBg, 0.10), borderColor: t.iconBg },
                  }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: "50%",
                    bgcolor: t.iconBg, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, flexShrink: 0,
                  }}>{i + 1}</Box>
                  <Box sx={{ width: 26, height: 26, borderRadius: "8px", bgcolor: alpha(t.iconBg, 0.15), color: t.accent, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 15 }, flexShrink: 0 }}>
                    {p.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: dashTokens.color.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.title}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: dashTokens.color.muted }}>
                      {p.hint}
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ fontSize: 14, color: t.accent, flexShrink: 0 }} />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ══════════ Today's Progress — solid sky/cyan fill (spec §3) */}
      <Box sx={{
        bgcolor: toneMap.sky.grad,
        border: `1px solid ${toneMap.sky.border}`,
        borderRadius: `${dashTokens.radius.lg}px`,
        boxShadow: dashTokens.shadow.card,
        p: 2, display: "flex", flexDirection: "column",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: "10px",
            bgcolor: toneMap.teal.iconBg, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 12px ${toneMap.teal.iconBg}40`,
            "& svg": { fontSize: 18 },
          }}>
            <CheckCircleOutlineIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: dashTokens.color.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Today's Progress
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: dashTokens.color.text }}>
              Live operational health
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
          {progressRow(
            "Classes", classesDone, classesTotal, classesPct,
            (v: number | null, tot: number) => v == null ? "—" : `${v} of ${tot}`,
            "blue",
          )}
          {progressRow(
            "Attendance",
            attendanceSubmitted, attendanceExpected, attendancePct,
            () => attendanceExpected > 0 ? `— of ${attendanceExpected}` : "—",
            "purple",
          )}
          {hasFinancePerm && progressRow(
            "Collections",
            paidToday, billedToday, collectionsPct,
            () => billedToday > 0 ? `${formatCurrencyCompact(paidToday)} of ${formatCurrencyCompact(billedToday)}` : "—",
            "green",
          )}
        </Box>

        {/* Optional Finance-attention footer row when overdue > 0 */}
        {hasFinancePerm && (summary.overdue_count ?? 0) > 0 && (
          <Box onClick={() => navigate("/invoice/list")}
            sx={{
              mt: 1.5, p: 1.25, borderRadius: "10px", cursor: "pointer",
              bgcolor: toneMap.amber.grad, border: `1px solid ${toneMap.amber.iconBg}30`,
              display: "flex", alignItems: "center", gap: 1,
              transition: dashTokens.transition,
              "&:hover": { transform: "translateX(2px)", borderColor: toneMap.amber.iconBg },
            }}>
            <ReceiptLongIcon sx={{ fontSize: 16, color: toneMap.amber.accent }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: toneMap.amber.accent, flex: 1 }}>
              {summary.overdue_count} overdue · {formatCurrency(summary.outstanding)}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 14, color: toneMap.amber.accent }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Small local helper for the Today's Progress rows.
const progressRow = (
  label: string,
  value: number | null,
  total: number,
  pct: number | null,
  fmt: (v: number | null, t: number) => string,
  tone: Tone,
) => {
  const t = toneMap[tone];
  return (
    <Box key={label}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: dashTokens.color.text }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.accent }}>
          {pct == null ? "—" : `${pct}%`}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct == null ? 0 : Math.min(100, Math.max(0, pct))}
        sx={{
          height: 6, borderRadius: 3,
          bgcolor: alpha(t.iconBg, 0.14),
          "& .MuiLinearProgress-bar": { bgcolor: t.iconBg, borderRadius: 3 },
        }}
      />
      <Typography sx={{ fontSize: 10.5, color: dashTokens.color.muted, mt: 0.5 }}>
        {fmt(value, total)}
      </Typography>
    </Box>
  );
};

export default DailyBriefingBar;
